/**
 * 표준 표기 추천 (v2.316.1 · 사용자 「납품항목 → SM 내부 명칭 → 표준 표기(나라장터쇼핑몰 표기)」 · 「추천 매칭도 해줘」).
 *
 * SM 내부 명칭(IFT 16TB) 하나에 나라장터 쇼핑몰 등록 명칭 후보(MAS 제품 풀네임 · 3자단가 규격)를 줄 세운다.
 * ⭐ 고르기 창 · 표준 표기 매칭 화면 · 「확실한 추천 한 번에 잇기」가 **이 파일 하나**를 쓴다 —
 *   같은 판정을 두 곳에 적으면 「화면엔 추천인데 한 번에 잇기엔 빠진다」로 갈린다.
 *
 * 나라장터 명칭은 「품명, 회사, 모델, 규격」 꼴이다(하드디스크드라이브, Infortrend technology, (TW)HELH72S3T16, 16TB).
 * ⭐ 「확실」은 사람이 하나하나 안 보고 한 번에 이어지므로 **가장 좁게**(독립 검토 두 번 — 가격만 같은 남의 제품이 확실로 떴다):
 *   디스크: 하드디스크 후보 · 단가 같음 · 용량 같음 · 회사 같음(IFT=Infortrend · EDS=Seagate) — 넷 다
 *   그 밖: 단가 같음 · 모델 이름이 **글자 그대로** 같음(후보의 모델 칸 · 「RS100-G」≠「RS100-E」 · 「US216」≠「US216D」)
 *   + 그런 후보가 **하나뿐**일 때.
 * 「추천」: 디스크 = 용량·회사 같음(단가는 달라도 · 회사를 모르면 용량·단가 같음) ·
 *         그 밖 = 모델 이름이 같음(단가는 달라도) 또는 회사 같고 모델 앞부분이 같음.
 * 「가격 같음」: 근거가 단가 하나뿐 — 다른 제품일 수 있다(사람이 본다).
 * ⚠ 반대 근거(디스크의 용량·회사가 다름)가 있으면 어떤 표시도 안 붙인다(IFT 16TB ↔ Seagate 16TB · EDS 16TB ↔ Seagate 20TB).
 * ⚠ 「SERVER」·「STANDARD」·「GOLD」·「XEON」 같은 흔한 낱말과 CPU 번호(5318Y)는 근거로 안 친다 — 윈도 라이선스·CPU 가 서버로 추천됐다.
 */

import type { DeliveryPriceItem, StandardNameCandidate } from './maintenance-api';

/** 회사 줄임말 — SM 내부 명칭은 줄여 쓴다(IFT 16TB = Infortrend · EDS 16TB = Seagate · 옛 SEA = Seagate) */
const VENDOR_ALIAS: Record<string, string> = { IFT: 'INFORTREND', EDS: 'SEAGATE', SEA: 'SEAGATE', WD: 'WESTERNDIGITAL' };

/** 점수에 쓰는 SM 내부 명칭 쪽 값 — 단가표 줄의 일부 */
export type StandardScoreItem = Pick<DeliveryPriceItem, 'label' | 'kind' | 'unitPrice' | 'capacityFromName'>;

export type StandardReason = 'price' | 'capacity' | 'vendor' | 'model' | 'family';

export interface StandardRanked {
  candidate: StandardNameCandidate;
  score: number;
  /** 사람이 읽는 근거 — 「단가 같음 · 16TB · INFORTREND」 */
  why: string[];
  /** 근거 종류 — 표시(확실·추천·가격 같음)를 가른다 */
  reasons: StandardReason[];
  /** 반대 근거(디스크 용량·회사가 다름) — 있으면 표시를 안 붙인다 */
  conflict: boolean;
  /** 이 후보 하나만 보면 「확실」 감인가 (하나뿐인지는 recommendStandard 가 본다) */
  sureAlone: boolean;
}

export interface StandardRecommendation {
  /** 1등 (「추천」·「가격 같음」 이상만 — 없으면 null) */
  best: StandardRanked | null;
  /** 2등 (같은 기준) */
  second: StandardRanked | null;
  /** 확실한 추천 — 한 번에 잇기 대상 */
  sure: boolean;
}

/** 견주기용 — 대문자 · 영문·숫자·「+」·「.」만 (「ES-650V2」=「ES650V2」 · 「5515+」≠「5515」) */
const norm = (s: string): string => s.toUpperCase().replace(/[^A-Z0-9+.]/g, '');
/** 나라장터 모델 칸 앞의 나라 표시 「(TW)」·「(CN) 」를 뗀다 */
const stripCountry = (s: string): string => s.trim().replace(/^\([A-Za-z]{2}\)\s*/, '');

const capOf = (s: string): string | null => {
  const all = [...s.toUpperCase().matchAll(/(\d+(?:\.\d+)?)\s*TB/g)];
  return all.length === 0 ? null : all[all.length - 1]![1]!;
};

/** 후보 나라장터 명칭 → 회사 · 모델 (쉼표 칸이 셋 미만이면 회사 모름 · 모델 = 통째) */
function candParts(c: StandardNameCandidate): { vendor: string | null; model: string } {
  const f = c.name.split(',').map((x) => x.trim());
  if (f.length >= 3) return { vendor: norm(f[1]!), model: norm(stripCountry(f[2]!)) };
  return { vendor: null, model: norm(stripCountry(c.name)) };
}

/** 모델 이름 낱말 — 글자로 시작하고 숫자가 든 3글자 넘는 것(SR650 · RS100-G · JB3060RL) · 용량(16TB)·CPU 번호(5318Y)는 빼고 */
function labelParts(label: string): { vendor: string | null; models: string[] } {
  const words = label.split(/[\s,]+/).map(stripCountry).filter((w) => w !== '');
  const first = words[0] ?? '';
  const vendor = /^[A-Za-z]{2,}$/.test(first) ? (VENDOR_ALIAS[first.toUpperCase()] ?? first.toUpperCase()) : null;
  const isModel = (w: string): boolean => /^[A-Za-z]/.test(w) && /\d/.test(w) && !/^\d+(?:\.\d+)?(?:TB|GB)$/i.test(w);
  const models = new Set<string>();
  words.forEach((w, i) => {
    if (!isModel(w)) return;
    const n = norm(w);
    if (n.length >= 3) models.add(n);
    // 「SR650 V2」·「RS100 G」처럼 띄어 쓴 꼬리(3글자 이하)를 붙인 것도 모델 이름
    const next = words[i + 1];
    if (next !== undefined && next.length <= 3 && /^[A-Za-z0-9]+$/.test(next)) models.add(norm(w + next));
  });
  // 띄어 쓴 모델 이름 통째 — 「X 5U84」 = 「X5U84」 (회사 이름으로 시작하면 통째는 모델이 아니다)
  const whole = norm(label);
  if (vendor === null && /[A-Z]/.test(whole) && /\d/.test(whole) && whole.length >= 4) models.add(whole);
  return { vendor, models: [...models] };
}

export function scoreStandard(it: StandardScoreItem, c: StandardNameCandidate): Omit<StandardRanked, 'candidate'> {
  const why: string[] = [];
  const reasons: StandardReason[] = [];
  let score = 0;
  let conflict = false;
  const price = it.unitPrice !== null && c.price !== null && it.unitPrice === c.price;
  if (price) { score += 3; why.push('단가 같음'); reasons.push('price'); }

  const cp = candParts(c);
  const lp = labelParts(it.label);
  const diskItem = it.kind === 'disk';
  const diskCand = /하드디스크/.test(c.name) || /하드디스크/.test(c.group);
  let sureAlone = false;

  if (diskItem) {
    // 디스크는 용량·회사로 본다 — 모델 칸(HELH72S3T16)은 SM 내부 명칭에 없다
    if (diskCand) {
      const a = it.capacityFromName !== null ? String(it.capacityFromName) : capOf(it.label);
      const b = capOf(c.name);
      if (a !== null && b !== null && Number(a) === Number(b)) { score += 2; why.push(`${a}TB`); reasons.push('capacity'); }
      else if (a !== null && b !== null) conflict = true;
      if (lp.vendor !== null && cp.vendor !== null) {
        if (cp.vendor.includes(norm(lp.vendor))) { score += 1; why.push(lp.vendor); reasons.push('vendor'); }
        else conflict = true;
      }
      sureAlone = price && reasons.includes('capacity') && reasons.includes('vendor') && !conflict;
    }
  } else {
    const exact = lp.models.includes(cp.model);
    // 모델 이름이 같은 것이 가격만 같은 것보다 앞 — RS100-G 는 가격이 같은 RS100-E 보다 RS100-G 가 먼저
    if (exact) { score += 4; why.push(c.name.split(',')[2]?.trim() || cp.model); reasons.push('model'); }
    else if (cp.model.length >= 4 && lp.models.some((m) => m.length >= 4 && (cp.model.startsWith(m) || m.startsWith(cp.model)))) {
      score += 1; why.push('모델 앞부분'); reasons.push('family');
    }
    if (lp.vendor !== null && cp.vendor !== null && cp.vendor.includes(norm(lp.vendor))) {
      score += 1; why.push(lp.vendor); reasons.push('vendor');
    }
    sureAlone = price && exact;
  }
  if (conflict) score -= 4;
  return { score, why, reasons, conflict, sureAlone };
}

/** 표시 — 확실·추천·가격 같음 (null 이면 표시 없음) */
export function standardBadge(r: StandardRanked, sure: boolean): 'sure' | 'recommend' | 'price' | null {
  if (r.conflict) return null;
  if (sure) return 'sure';
  const has = (x: StandardReason): boolean => r.reasons.includes(x);
  if (has('capacity') && has('vendor')) return 'recommend';
  if (has('capacity') && has('price')) return 'recommend'; // 회사를 모르는 디스크(「16TB HDD」)
  if (has('model')) return 'recommend';
  if (has('family') && has('vendor')) return 'recommend';
  if (has('price')) return 'price';
  return null;
}

/** 후보 전부를 점수 순으로 (같으면 목록 순서) */
export function rankStandard(it: StandardScoreItem, names: StandardNameCandidate[]): StandardRanked[] {
  return names
    .map((candidate, i) => ({ candidate, i, ...scoreStandard(it, candidate) }))
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map(({ i: _i, ...r }) => r);
}

/** 이 SM 내부 명칭의 추천 — 1등·2등과 「확실」 여부 (확실 감이 **하나뿐**일 때만 확실) */
export function recommendStandard(it: StandardScoreItem, names: StandardNameCandidate[]): StandardRecommendation {
  const ranked = rankStandard(it, names);
  const top = ranked.filter((r) => standardBadge(r, false) !== null);
  const sureOnes = ranked.filter((r) => r.sureAlone);
  if (sureOnes.length === 1) {
    const best = sureOnes[0]!;
    return { best, second: top.find((r) => r !== best) ?? null, sure: true };
  }
  return { best: top[0] ?? null, second: top[1] ?? null, sure: false };
}

/**
 * 이을 수 있는 품목인가 — 서버도 거절한다.
 *   · 영문·숫자가 없는 품목(서버랙·프론트커버)은 대조 열쇠가 없다
 *   · 쉼표·괄호가 들었거나 60자가 넘는 이름(「Lenovo, X3650M5」·「WIN SVR 2022 STD (16C)」)은 단가 찾기가 쪼개거나
 *     괄호를 떼어 읽어서, 이어 둬도 문서엔 글 그대로다(서버 docConvertible · 옛 서버는 값이 없어 이을 수 있는 것으로 본다)
 */
export function standardLinkable(it: Pick<DeliveryPriceItem, 'key' | 'docConvertible'>): boolean {
  return !it.key.startsWith('#') && it.docConvertible !== false;
}

/** 이을 수 없는 까닭 — 화면 한 줄 */
export function standardUnlinkableReason(it: Pick<DeliveryPriceItem, 'key'>): string {
  return it.key.startsWith('#')
    ? '영문·숫자가 없어 이을 수 없음 (문서엔 글 그대로 · ⚠ 없음)'
    : '쉼표·괄호가 들었거나 60자가 넘는 이름 — 문서에서 표준 표기로 못 바꿈(글 그대로)';
}

/**
 * 「등록 명칭 없음 — 글 그대로」로 정한 품목 — 표준 표기가 **이을 때 본 SM 내부 명칭 자신**.
 * 빈칸 하나까지 견준다(서버 isSelfStandard 와 같은 규칙) — 「X5U84」는 「X 5U84」의 등록 명칭이지 「그대로」가 아니다.
 */
export function standardIsSelf(it: Pick<DeliveryPriceItem, 'label' | 'standardName' | 'standardLabel'>): boolean {
  if (!it.standardName) return false;
  const c = (s: string): string => s.replace(/\s+/g, ' ').trim().toUpperCase();
  return c(it.standardName) === c(it.standardLabel ?? it.label);
}

/** 괄호·대괄호 밖의 쉼표로만 나눈 칸들 (서버 delivery-price.ts splitTopLevel 과 같은 규칙) */
function splitTopLevel(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of text) {
    if (ch === '(' || ch === '[') depth += 1;
    else if ((ch === ')' || ch === ']') && depth > 0) depth -= 1;
    if (ch === ',' && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim()).filter((s) => s !== '');
}

/**
 * 문서에 적는 표준 표기 — 나라장터 명칭 맨 앞의 품명(하드디스크드라이브·디스크어레이·컴퓨터서버…)을 뺀다
 * (v2.316.2 · 사용자 10-31 「표준표기 내용을 좀 줄이자」). 서버 standardDocName 과 **같은 규칙** — check:budget-standard 가 견준다.
 *   하드디스크드라이브, Seagate, (MX)1105048-01, 20TB → Seagate, (MX)1105048-01, 20TB
 * 괄호 밖 쉼표 칸이 셋 이상이고 첫 칸이 한글뿐일 때만 뺀다(「Lenovo, X3650M5」·「그대로」로 정한 SM 내부 명칭은 그대로).
 */
export function standardDocName(standard: string): string {
  const s = standard.trim();
  const fields = splitTopLevel(s);
  if (fields.length < 3 || !/^[가-힣]+(?: [가-힣]+)*$/.test(fields[0]!)) return s;
  return s.slice(s.indexOf(',') + 1).trim();
}

/** 나라장터 명칭 맨 앞의 품명 — 문서엔 안 적지만 고를 때 보탬이 된다(없으면 null) */
export function standardCategoryOf(standard: string): string | null {
  const short = standardDocName(standard);
  return short === standard.trim() ? null : splitTopLevel(standard)[0] ?? null;
}

/** 글자 그대로 견주는 열쇠 — 서버 standardNameKey 와 같은 규칙(대문자 · 빈칸·「-」·「_」만 뺀다) */
export function standardNameKey(s: string): string {
  return s.toUpperCase().replace(/[\s_-]+/g, '');
}

/**
 * 이을 때 본 이름이 지금 목록 이름과 글자가 다른가 — 같은 단가표 열쇠의 다른 표기(1.8TB ↔ 18TB · 「Lenovo 데스크탑」 ↔ 「Lenovo」).
 * 문서는 이은 이름과 글자가 같은 품목 글만 바꾸므로, 지금 이름의 글은 안 바뀐다.
 */
export function standardLabelDiffers(it: Pick<DeliveryPriceItem, 'label' | 'standardName' | 'standardLabel'>): boolean {
  return !!it.standardName && !!it.standardLabel && standardNameKey(it.standardLabel) !== standardNameKey(it.label);
}
